import { API_URL } from "./config";
import { getToken, refreshSession, endSession } from "./auth";

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/**
 * GET responses keyed by path, with the ETag the server returned. A refetch sends
 * it as `If-None-Match`; a 304 then replays the cached body instead of
 * re-downloading an unchanged page (plan §3.5).
 */
const etagCache = new Map<string, { etag: string; body: unknown }>();

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  // The token is read per attempt, not once: the retry below must pick up the
  // freshly minted one rather than replay the expired header.
  const doFetch = () => {
    const token = getToken();
    const headers = new Headers(init.headers);
    if (!headers.has("Content-Type") && init.body) {
      headers.set("Content-Type", "application/json");
    }
    if (method === "GET") {
      const cached = etagCache.get(path);
      if (cached) headers.set("If-None-Match", cached.etag);
    }
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return fetch(`${API_URL}${path}`, { ...init, headers });
  };

  const hadToken = getToken() != null;
  let res = await doFetch();
  // A 401 usually just means the 1-hour access token expired mid-work. Renew it
  // silently from the httpOnly refresh cookie and retry once — only a 401 that
  // survives that means the session is really gone.
  if (res.status === 401) {
    if (await refreshSession()) res = await doFetch();
    if (res.status === 401) {
      // Tear the session down only if there WAS one to lose. A 401 with no token
      // is the ordinary "not signed in yet" boot probe — and with auth disabled
      // locally, /auth/check answers without a token at all. Reporting
      // "session expired" there would put a misleading message on the login
      // screen of a user who was never signed in.
      if (hadToken) endSession({ reason: "session-expired" });
      throw new ApiError(401, "Not authenticated");
    }
  }
  // 304 is not "ok" to fetch, but it is a successful cache hit.
  if (res.status === 304) {
    const cached = etagCache.get(path);
    if (cached) return cached.body as T;
    // A 304 we can't satisfy means a stale header; refetch unconditionally.
    res = await doFetch();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    const detail =
      typeof body.detail === "string"
        ? body.detail
        : registryMissMessage(body.detail) ?? `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  const contentType = res.headers.get("content-type") ?? "";
  const parsed: T = contentType.includes("text/plain") || contentType.includes("text/markdown")
    ? ((await res.text()) as T)
    : ((await res.json()) as T);
  if (method === "GET") {
    const etag = res.headers.get("etag");
    if (etag) etagCache.set(path, { etag, body: parsed });
  }
  return parsed;
}

/** A 422 from a registry tag or filter that didn't resolve (see qa_run_ledger.registry). */
function registryMissMessage(detail: unknown): string | null {
  if (!detail || typeof detail !== "object") return null;
  const d = detail as { error?: unknown; catalog?: unknown; unknown_tokens?: unknown };
  if (typeof d.error !== "string" || !d.error.startsWith("unknown_")) return null;
  const tokens = Array.isArray(d.unknown_tokens) ? d.unknown_tokens.join(", ") : "";
  return `Not a known ${String(d.catalog)} in the product registry: ${tokens}`;
}
